import { queryClient } from '@/query-client';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import {
  type Conversation,
  ConversationSchema,
  type Conversations,
  type ConversationsQuery,
  ConversationsSchema,
  type CreateConversation,
} from '@mai/types';

import { apiClient } from './axios';

import { logger } from '@utils/logger';

export const CONVERSATIONS_QUERY_KEY = 'conversations';

export const useConversationsQuery = (
  params: ConversationsQuery,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [CONVERSATIONS_QUERY_KEY, params],
    queryFn: async () => {
      const response = await apiClient.get<Conversations>('/conversations', {
        params,
      });
      const validatedData = ConversationsSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate conversations data', {
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled,
  });
};

export const useInfiniteConversationsQuery = (
  params: ConversationsQuery,
  enabled: boolean,
) => {
  return useInfiniteQuery({
    queryKey: [CONVERSATIONS_QUERY_KEY, params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient.get<Conversations>('/conversations', {
        params: { ...params, page: pageParam },
      });
      const validatedData = ConversationsSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate conversations data', {
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    getNextPageParam: (_, pages) => pages.length + 1,
    initialPageParam: 0,
    enabled,
  });
};

export const useConversationQuery = (
  conversationId: string | undefined | null,
) => {
  return useQuery({
    queryKey: [CONVERSATIONS_QUERY_KEY, conversationId],
    queryFn: async () => {
      const response = await apiClient.get<Conversation>(
        `/conversations/${conversationId}`,
      );
      const validatedData = ConversationSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate conversation data', {
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled: !!conversationId,
  });
};

export const createConversation = async (
  body: CreateConversation,
): Promise<Conversation> => {
  const response = await apiClient.post<Conversation>('/conversations', body);
  const validatedData = ConversationSchema.safeParse(response.data);
  if (!validatedData.success) {
    logger.warn('failed to validate conversation data', {
      error: validatedData.error,
    });
    return response.data;
  }
  await queryClient.invalidateQueries({ queryKey: [CONVERSATIONS_QUERY_KEY] });
  return validatedData.data;
};

export const deleteConversation = async (conversationId: string) => {
  await apiClient.delete(`/conversations/${conversationId}`);
  await queryClient.invalidateQueries({ queryKey: [CONVERSATIONS_QUERY_KEY] });
};
